import React, { useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

/**
 * BrandVideo — Cinematic in-page video player for the EBG brand film.
 * - Custom poster (thumbnail) with luxury frame
 * - Big gold play button + progress dots
 * - Click to play/pause native <video> (mp4), inline (no modal)
 * - Editorial dark section for maximum impact
 */
export default function BrandVideo() {
    const ref = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(false);

    const toggle = () => {
        const v = ref.current;
        if (!v) return;
        if (v.paused) {
            // With preload="none" the sources aren't loaded until play — ensure load first.
            if (v.readyState === 0) v.load();
            v.play().catch(() => {});
            setPlaying(true);
        } else { v.pause(); setPlaying(false); }
    };
    const toggleMute = (e) => {
        e.stopPropagation();
        const v = ref.current;
        if (!v) return;
        v.muted = !v.muted;
        setMuted(v.muted);
    };

    return (
        <section
            data-testid="home-brand-video"
            className="relative bg-black overflow-hidden"
        >
            {/* ambient gold glow */}
            <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-[0.08]"
                 style={{ background: "radial-gradient(circle, var(--ebg-gold) 0%, transparent 60%)" }}/>

            <div className="relative max-w-[1440px] mx-auto px-5 sm:px-6 md:px-10 py-16 md:py-24">
                {/* eyebrow */}
                <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
                    <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]" data-testid="brand-video-eyebrow">
                        Our Shared Vision
                    </div>
                    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl mt-3 md:mt-4 leading-[1.05] text-white">
                        Why we built <em className="not-italic text-[var(--ebg-gold)]">EBG Realty</em>.
                    </h2>
                    <p className="text-white/60 mt-4 md:mt-5 text-sm md:text-base leading-relaxed max-w-xl mx-auto">
                        India&rsquo;s real estate deserved a Trust-First platform. Watch our story
                        &mdash; and the vision that&rsquo;s bringing every buyer, builder and channel
                        partner onto one legally verified marketplace.
                    </p>
                </div>

                {/* Video card */}
                <div className="relative group max-w-5xl mx-auto">
                    {/* gold corner frames */}
                    <div className="absolute -top-3 -left-3 w-14 h-14 border-t-2 border-l-2 border-[var(--ebg-gold)] hidden sm:block"/>
                    <div className="absolute -bottom-3 -right-3 w-14 h-14 border-b-2 border-r-2 border-[var(--ebg-gold)] hidden sm:block"/>

                    <div
                        onClick={toggle}
                        data-testid="brand-video-frame"
                        className="relative aspect-video bg-black overflow-hidden cursor-pointer border border-white/10 shadow-[0_25px_80px_-20px_rgba(212,175,106,0.25)]"
                    >
                        <video
                            ref={ref}
                            poster="/video/brand-thumb.jpg"
                            preload="none"
                            playsInline
                            className="w-full h-full object-cover"
                            onPlay={() => setPlaying(true)}
                            onPause={() => setPlaying(false)}
                            onEnded={() => setPlaying(false)}
                        >
                            <source src="/video/ebg-brand.mp4" type="video/mp4"/>
                        </video>
                        {/* Poster overlay when paused */}
                        {!playing && (
                            <>
                                <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/55 pointer-events-none"/>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <button
                                        data-testid="brand-video-play"
                                        aria-label="Play brand video"
                                        className="relative w-16 h-16 sm:w-20 sm:h-20 md:w-28 md:h-28 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center shadow-[0_0_60px_rgba(212,175,106,0.55)] hover:scale-105 transition-transform duration-300"
                                    >
                                        <span className="absolute inset-0 rounded-full bg-[var(--ebg-gold)] animate-ping opacity-30"/>
                                        <Play size={22} className="ml-0.5 sm:w-7 sm:h-7 md:w-10 md:h-10 md:ml-2" fill="currentColor"/>
                                    </button>
                                </div>
                                {/* bottom caption strip on poster — compact on mobile */}
                                <div className="absolute left-0 right-0 bottom-0 px-4 md:px-8 py-2.5 md:py-5 bg-gradient-to-t from-black/85 to-transparent">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-[9px] md:text-[10px] uppercase tracking-[0.24em] md:tracking-[0.28em] text-[var(--ebg-gold)] font-bold truncate">EBG Realty · Our Shared Vision</div>
                                            <div className="hidden md:block text-white font-serif text-lg md:text-2xl mt-1">India&rsquo;s most trusted real estate platform.</div>
                                        </div>
                                        <div className="text-white/70 text-[9px] md:text-[11px] uppercase tracking-[0.2em] md:tracking-[0.22em] whitespace-nowrap shrink-0">57 sec &nbsp;·&nbsp; Watch</div>
                                    </div>
                                </div>
                            </>
                        )}
                        {/* Mini controls when playing */}
                        {playing && (
                            <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={(e) => { e.stopPropagation(); toggle(); }}
                                    className="w-10 h-10 bg-black/60 hover:bg-black text-white flex items-center justify-center backdrop-blur"
                                    aria-label="Pause"
                                >
                                    <Pause size={16}/>
                                </button>
                                <button
                                    onClick={toggleMute}
                                    className="w-10 h-10 bg-black/60 hover:bg-black text-white flex items-center justify-center backdrop-blur"
                                    aria-label={muted ? "Unmute" : "Mute"}
                                >
                                    {muted ? <VolumeX size={16}/> : <Volume2 size={16}/>}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* trust chips below */}
                <div className="mt-10 md:mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-white/55">
                    <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[var(--ebg-gold)]"/> Legal-Verified Listings</span>
                    <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[var(--ebg-gold)]"/> RERA-Registered Projects</span>
                    <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[var(--ebg-gold)]"/> 8-Factor Trust Score</span>
                    <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[var(--ebg-gold)]"/> Zero-Brokerage on New Launches</span>
                </div>
            </div>
        </section>
    );
}
